/**
 * StaffPortalPage.jsx — TaxSetu AI Staff Review Portal
 *
 * Limited-access portal for staff/reviewers. They can:
 *   • Review past AI responses (anonymized, no user emails)
 *   • Submit corrections that flow to admin approval queue
 *   • Track their own submission history
 *
 * Privacy: Staff NEVER see user_email — only "User #ABC123" anonymized hashes.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Inbox,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  LogOut,
  Search,
  AlertCircle,
  Sparkles,
  ChevronRight,
  Loader2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_KEY = "taxsetu_staff_token";
const NAME_KEY = "taxsetu_staff_name";
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_taxgpt-india-1/artifacts/eq72g3c3_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

export default function StaffPortalPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEY) || "");
  const [name, setName] = useState(localStorage.getItem(NAME_KEY) || "");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [activeTab, setActiveTab] = useState("review"); // review | submissions
  const [queries, setQueries] = useState([]);
  const [submissions, setSubmissions] = useState({ items: [], counts: {} });
  const [totalPending, setTotalPending] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    action: "needs_improvement",
    suggested_answer: "",
    reasoning: "",
    references: "",
  });
  const [submissionFilter, setSubmissionFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Login failed");
        return;
      }
      localStorage.setItem(STORAGE_KEY, data.token);
      localStorage.setItem(NAME_KEY, data.name || "");
      setToken(data.token);
      setName(data.name || "");
      toast.success(`Welcome, ${data.name || "Staff"}`);
    } catch (err) {
      toast.error("Login error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/staff/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (_) {}
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(NAME_KEY);
    setToken("");
    setName("");
    navigate("/");
  };

  // Data loaders
  const loadQueries = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/queries-to-review?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        handleLogout();
        return;
      }
      const data = await res.json();
      setQueries(data.queries || []);
      setTotalPending(data.total_pending || 0);
    } catch (err) {
      toast.error("Failed to load queries");
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/my-submissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        handleLogout();
        return;
      }
      const data = await res.json();
      setSubmissions({ items: data.submissions || [], counts: data.counts || {} });
    } catch (err) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    if (activeTab === "review") loadQueries();
    else loadSubmissions();
  }, [token, activeTab]);

  const submitReview = async () => {
    if (!selectedQuery) return;
    if (
      reviewForm.action === "needs_improvement" &&
      !reviewForm.suggested_answer.trim()
    ) {
      toast.error("Please provide a suggested answer");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/submit-review`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query_id: selectedQuery.id,
          ...reviewForm,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Submit failed");
        return;
      }
      toast.success(
        reviewForm.action === "correct"
          ? "Marked as correct ✓"
          : "Submitted for admin approval"
      );
      // Remove from local list
      setQueries((prev) => prev.filter((q) => q.id !== selectedQuery.id));
      setTotalPending((p) => Math.max(0, p - 1));
      setSelectedQuery(null);
      setReviewForm({
        action: "needs_improvement",
        suggested_answer: "",
        reasoning: "",
        references: "",
      });
    } catch (err) {
      toast.error("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── LOGIN VIEW ───────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/5 backdrop-blur border border-white/10 mb-4 p-3">
              <img src={TAXSETU_LOGO} alt="TaxSetu AI" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-1">TaxSetu AI Staff</h1>
            <p className="text-slate-400 text-sm">Sign in to review queries</p>
          </div>
          <form
            onSubmit={handleLogin}
            className="bg-slate-900/60 backdrop-blur border border-slate-800 rounded-2xl p-6 space-y-4"
            data-testid="staff-login-form"
          >
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block uppercase tracking-wider font-semibold">
                Email
              </label>
              <input
                type="email"
                required
                value={loginForm.email}
                onChange={(e) =>
                  setLoginForm((p) => ({ ...p, email: e.target.value }))
                }
                className="w-full px-3 py-2.5 rounded-lg bg-slate-950/60 border border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 outline-none text-sm"
                placeholder="support@taxsetu.ai"
                data-testid="staff-email"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block uppercase tracking-wider font-semibold">
                Password
              </label>
              <input
                type="password"
                required
                value={loginForm.password}
                onChange={(e) =>
                  setLoginForm((p) => ({ ...p, password: e.target.value }))
                }
                className="w-full px-3 py-2.5 rounded-lg bg-slate-950/60 border border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 outline-none text-sm"
                placeholder="••••••••"
                data-testid="staff-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold hover:opacity-90 transition disabled:opacity-50"
              data-testid="staff-login-submit"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
            <p className="text-[11px] text-slate-500 text-center pt-2">
              Need access? Ask your admin to create your account.
            </p>
          </form>
        </div>
      </div>
    );
  }

  // ─── MAIN PORTAL ──────────────────────────────────────────────────────
  const filteredQueries = queries.filter((q) => {
    if (!searchTerm) return true;
    return (q.query || "").toLowerCase().includes(searchTerm.toLowerCase());
  });

  const filteredSubmissions = submissions.items.filter((s) => {
    if (submissionFilter === "all") return true;
    return s.status === submissionFilter;
  });

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center p-1.5">
              <img src={TAXSETU_LOGO} alt="TaxSetu AI" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-base font-bold">TaxSetu AI Staff Portal</h1>
              <p className="text-[11px] text-slate-400">{name}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-slate-800 transition"
            data-testid="staff-logout"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-slate-800 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 flex gap-1">
          <button
            onClick={() => setActiveTab("review")}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === "review"
                ? "border-cyan-500 text-cyan-300"
                : "border-transparent text-slate-400 hover:text-white"
            }`}
            data-testid="tab-review"
          >
            <Inbox className="w-4 h-4" /> Queries to Review
            {totalPending > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-cyan-500/20 text-cyan-300 font-semibold">
                {totalPending}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("submissions")}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === "submissions"
                ? "border-cyan-500 text-cyan-300"
                : "border-transparent text-slate-400 hover:text-white"
            }`}
            data-testid="tab-submissions"
          >
            <Send className="w-4 h-4" /> My Submissions
            {submissions.counts.pending_admin > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-amber-500/20 text-amber-300 font-semibold">
                {submissions.counts.pending_admin} pending
              </span>
            )}
          </button>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* ─── REVIEW TAB ─────────────────────────────────────────────── */}
        {activeTab === "review" && (
          <>
            <div className="mb-4 flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search queries…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-sm text-white placeholder:text-slate-500 focus:border-cyan-500 outline-none"
                />
              </div>
              <span className="text-xs text-slate-500">
                Showing {filteredQueries.length} of {totalPending} total
              </span>
            </div>

            {loading && queries.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
                Loading queries…
              </div>
            ) : filteredQueries.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                <p className="font-medium">All caught up!</p>
                <p className="text-xs mt-1">
                  No more queries to review right now.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredQueries.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => setSelectedQuery(q)}
                    className="w-full text-left bg-slate-900/40 hover:bg-slate-900/80 border border-slate-800 hover:border-cyan-500/40 rounded-lg p-3 transition group"
                    data-testid={`query-card-${q.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                          {q.user_id_hash}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium truncate">
                          {q.query}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500 uppercase tracking-wider">
                          <span>{q.feature || "chat"}</span>
                          {q.is_follow_up && (
                            <span className="px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">
                              follow-up
                            </span>
                          )}
                          {q.attachment_count > 0 && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">
                              📎 {q.attachment_count}
                            </span>
                          )}
                          <span>•</span>
                          <span>
                            {q.timestamp
                              ? new Date(q.timestamp).toLocaleString()
                              : ""}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 flex-shrink-0 mt-1" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* ─── SUBMISSIONS TAB ────────────────────────────────────────── */}
        {activeTab === "submissions" && (
          <>
            <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-1">
              {[
                { key: "all", label: "All", count: submissions.items.length },
                { key: "pending_admin", label: "Pending Admin", count: submissions.counts.pending_admin || 0, color: "amber" },
                { key: "approved", label: "Approved", count: submissions.counts.approved || 0, color: "emerald" },
                { key: "rejected", label: "Rejected", count: submissions.counts.rejected || 0, color: "rose" },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setSubmissionFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border transition whitespace-nowrap ${
                    submissionFilter === f.key
                      ? "bg-cyan-500/20 border-cyan-500/60 text-cyan-200"
                      : "bg-slate-900/40 border-slate-800 text-slate-400 hover:border-slate-700"
                  }`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label} <span className="ml-1 opacity-70">{f.count}</span>
                </button>
              ))}
            </div>

            {filteredSubmissions.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <Inbox className="w-10 h-10 mx-auto mb-2" />
                <p>No submissions yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredSubmissions.map((s) => {
                  const statusConfig = {
                    pending_admin: { color: "amber", icon: Clock, label: "Pending Admin Approval" },
                    approved: { color: "emerald", icon: CheckCircle2, label: "Approved" },
                    rejected: { color: "rose", icon: XCircle, label: "Rejected" },
                  };
                  const sc = statusConfig[s.status] || statusConfig.pending_admin;
                  const Icon = sc.icon;
                  return (
                    <div
                      key={s.id}
                      className="bg-slate-900/40 border border-slate-800 rounded-lg p-4"
                      data-testid={`submission-${s.id}`}
                    >
                      <div className="flex items-start justify-between mb-2 gap-3">
                        <p className="text-sm text-white font-medium flex-1 min-w-0">
                          {s.original_query}
                        </p>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase tracking-wider flex-shrink-0 bg-${sc.color}-500/20 text-${sc.color}-300 border border-${sc.color}-500/40`}
                        >
                          <Icon className="w-3 h-3" /> {sc.label}
                        </span>
                      </div>
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
                        Action: {s.action.replace(/_/g, " ")}
                      </p>
                      {s.suggested_answer && (
                        <details className="text-xs text-slate-300">
                          <summary className="cursor-pointer text-cyan-400 hover:text-cyan-300">
                            View suggestion ↓
                          </summary>
                          <div className="mt-2 p-3 bg-slate-950/50 rounded text-slate-300 whitespace-pre-wrap leading-relaxed">
                            {s.suggested_answer}
                          </div>
                          {s.reasoning && (
                            <p className="mt-2 text-[11px] text-slate-400 italic">
                              <strong>Reasoning:</strong> {s.reasoning}
                            </p>
                          )}
                        </details>
                      )}
                      {s.admin_note && (
                        <div className="mt-2 p-2 rounded bg-slate-950/40 border-l-2 border-cyan-500 text-[11px] text-slate-300">
                          <strong className="text-cyan-300">Admin:</strong>{" "}
                          {s.admin_note}
                        </div>
                      )}
                      <p className="text-[10px] text-slate-500 mt-2">
                        Submitted: {s.created_at?.substring(0, 19).replace("T", " ")}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {/* ─── REVIEW MODAL ────────────────────────────────────────────── */}
      {selectedQuery && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedQuery(null)}
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-slate-800 bg-gradient-to-r from-cyan-600/10 to-blue-600/10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono px-2 py-1 rounded bg-slate-800 text-slate-400">
                  {selectedQuery.user_id_hash}
                </span>
                <button
                  onClick={() => setSelectedQuery(null)}
                  className="text-slate-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              <h3 className="text-base font-semibold text-white leading-snug">
                {selectedQuery.query}
              </h3>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-amber-400 mb-1.5 font-semibold">
                  ⚙️ Current AI Response
                </p>
                <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800 text-[13px] text-slate-300 whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {selectedQuery.full_response || selectedQuery.response_preview || "(no response captured)"}
                </div>
              </div>

              <div className="border-t border-slate-800 pt-4">
                <p className="text-[10px] uppercase tracking-wider text-cyan-400 mb-2 font-semibold">
                  🎯 Your Review
                </p>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[
                    { key: "correct", label: "✓ Correct", color: "emerald" },
                    { key: "needs_improvement", label: "⚠ Needs Improvement", color: "amber" },
                    { key: "add_knowledge", label: "+ Add Knowledge", color: "purple" },
                  ].map((a) => (
                    <button
                      key={a.key}
                      onClick={() => setReviewForm((p) => ({ ...p, action: a.key }))}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold border transition ${
                        reviewForm.action === a.key
                          ? `bg-${a.color}-500/20 border-${a.color}-500/60 text-${a.color}-200`
                          : "bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700"
                      }`}
                      data-testid={`action-${a.key}`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>

                {reviewForm.action !== "correct" && (
                  <>
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 mb-1 block font-semibold">
                      Suggested Answer *
                    </label>
                    <textarea
                      rows={6}
                      value={reviewForm.suggested_answer}
                      onChange={(e) =>
                        setReviewForm((p) => ({ ...p, suggested_answer: e.target.value }))
                      }
                      className="w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm focus:border-cyan-500 outline-none mb-3"
                      placeholder="Write a better/more accurate answer here…"
                      data-testid="suggested-answer"
                    />
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 mb-1 block font-semibold">
                      Reasoning (why is the original wrong?)
                    </label>
                    <textarea
                      rows={2}
                      value={reviewForm.reasoning}
                      onChange={(e) =>
                        setReviewForm((p) => ({ ...p, reasoning: e.target.value }))
                      }
                      className="w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm focus:border-cyan-500 outline-none mb-3"
                      placeholder="Brief explanation of the correction…"
                    />
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 mb-1 block font-semibold">
                      References (Section, Notification, URL)
                    </label>
                    <input
                      type="text"
                      value={reviewForm.references}
                      onChange={(e) =>
                        setReviewForm((p) => ({ ...p, references: e.target.value }))
                      }
                      className="w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm focus:border-cyan-500 outline-none"
                      placeholder="e.g. Section 80C IT Act 1961, CBDT Notification 2026/XX"
                    />
                  </>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-slate-800 bg-slate-900/60 flex items-center justify-end gap-2">
              <button
                onClick={() => setSelectedQuery(null)}
                className="px-4 py-2 text-sm text-slate-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={submitReview}
                disabled={loading}
                className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                data-testid="submit-review-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {reviewForm.action === "correct" ? "Mark Correct" : "Submit for Approval"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
